import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Trash2, Clock, RefreshCw, Search, Shield, KeyRound, AlertTriangle } from 'lucide-react';
import { tempGuestAPI, entitiesAPI } from '../../services/api';
import { CreateTempGuestDialog } from '../../components/guest/CreateTempGuestDialog';
import { useAuth } from '../../contexts/AuthContext';

const MODULE_LABELS = {
  ropa_intake: 'ROPA Intake',
  breach_intake: 'Breach Intake',
  dpia_intake: 'DPIA Intake',
  prescreen: 'Pre-Screen',
  readiness_assessment: 'Readiness Assessment',
  questionnaire: 'Questionnaire',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function TempGuestManagementPage() {
  const { user, isDPO, isClientAdmin, isEntityAdmin } = useAuth();
  const [guests, setGuests] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [extendGuest, setExtendGuest] = useState(null);
  const [extendDays, setExtendDays] = useState('14');
  const [extending, setExtending] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  useEffect(() => {
    fetchGuests();
    if (isDPO || isClientAdmin) {
      entitiesAPI.getAll(isClientAdmin ? user?.client_id : undefined)
        .then(res => setEntities(res.data || []))
        .catch(() => {});
    } else if (isEntityAdmin && user?.entity_id) {
      setEntities([{ id: user.entity_id, entity_name: user.entity_name || 'Your Entity' }]);
    }
  }, []);

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const res = await tempGuestAPI.list();
      setGuests(res.data.guests || []);
    } catch {
      toast.error('Failed to load guest accounts.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(revokeTarget.id);
    try {
      await tempGuestAPI.revoke(revokeTarget.id);
      toast.success('Access revoked.');
      setRevokeTarget(null);
      fetchGuests();
    } catch {
      toast.error('Failed to revoke access.');
    } finally {
      setRevoking(null);
    }
  };

  const handleExtend = async () => {
    if (!extendGuest) return;
    setExtending(true);
    try {
      const res = await tempGuestAPI.extend(extendGuest.id, parseInt(extendDays, 10));
      toast.success(`Access extended to ${formatDate(res.data.new_expires_at)}`);
      setExtendGuest(null);
      fetchGuests();
    } catch {
      toast.error('Failed to extend access.');
    } finally {
      setExtending(false);
    }
  };

  const filtered = guests.filter(g => {
    const matchSearch = !search || g.email.toLowerCase().includes(search.toLowerCase()) || g.full_name?.toLowerCase().includes(search.toLowerCase());
    const gModules = g.guest_modules || [];
    const matchModule = moduleFilter === 'all' || gModules.includes(moduleFilter);
    return matchSearch && matchModule;
  });

  const now = new Date();

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Temp Guest Access</h1>
            <p className="text-muted-foreground">Manage time-limited guest accounts for external form submissions.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="gap-2 bg-violet-600 hover:bg-violet-700 text-white">
            <UserPlus className="h-4 w-4" />
            Create Temp Login
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modules</SelectItem>
              {Object.entries(MODULE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={fetchGuests} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">No guest accounts found.</div>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map(g => {
                  const expired = g.is_expired || !g.is_active;
                  const expiresAt = g.guest_expires_at ? new Date(g.guest_expires_at) : null;
                  const daysLeft = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;

                  return (
                    <div key={g.id} className="flex items-center justify-between px-4 py-3 gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{g.full_name || g.email}</p>
                          {g.guest_auth_method === 'otp'
                            ? <Shield className="h-3 w-3 text-violet-400 shrink-0" title="OTP" />
                            : <KeyRound className="h-3 w-3 text-slate-400 shrink-0" title="Password" />
                          }
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{g.email}</p>
                      </div>

                      <div className="hidden sm:flex shrink-0 flex-wrap gap-1 max-w-[200px] justify-end">
                        {(g.guest_modules || []).map(m => (
                          <Badge key={m} variant="outline" className="text-xs">
                            {MODULE_LABELS[m] || m}
                          </Badge>
                        ))}
                      </div>

                      <div className="shrink-0 text-right">
                        {expired ? (
                          <Badge variant="outline" className="text-xs bg-rose-500/10 text-rose-400 border-rose-500/20">Expired</Badge>
                        ) : daysLeft !== null && daysLeft <= 3 ? (
                          <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/20">
                            <Clock className="h-3 w-3 mr-1" />{daysLeft}d left
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Expires {formatDate(g.guest_expires_at)}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setExtendGuest(g); setExtendDays('14'); }}
                          className="text-xs gap-1"
                        >
                          <Clock className="h-3 w-3" />
                          Extend
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRevokeTarget(g)}
                          disabled={revoking === g.id || expired}
                          className="text-xs text-destructive hover:text-destructive gap-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          Revoke
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <CreateTempGuestDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => fetchGuests()}
        entities={entities}
        lockedEntityId={isEntityAdmin ? user?.entity_id : null}
      />

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Revoke guest access
            </DialogTitle>
            <DialogDescription>
              You are about to revoke access for <strong>{revokeTarget?.email}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-sm text-amber-600 dark:text-amber-400 space-y-1">
              <p className="font-medium">This will immediately:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Block the guest from logging in</li>
                <li>Invalidate any active login session</li>
                <li>Delete any pending OTP codes</li>
              </ul>
              <p className="text-xs mt-2">If the guest is currently filling out a form, any unsaved progress will be lost. Already submitted records are not affected.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRevokeTarget(null)} className="flex-1">Cancel</Button>
              <Button
                onClick={handleRevoke}
                disabled={!!revoking}
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                {revoking ? 'Revoking…' : 'Revoke access'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extend dialog */}
      <Dialog open={!!extendGuest} onOpenChange={() => setExtendGuest(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Extend access</DialogTitle>
            <DialogDescription>
              Extend access for <strong>{extendGuest?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Select value={extendDays} onValueChange={setExtendDays}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[7, 14, 30, 60, 90].map(d => (
                  <SelectItem key={d} value={String(d)}>+{d} days</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setExtendGuest(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleExtend} disabled={extending} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white">
                {extending ? 'Extending…' : 'Extend'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
